import { Injectable, inject } from '@angular/core';
import { SideNavGroup, SideNavUser } from '@umss/shared/ui';
import { AuthService, AuthStorageService } from '@umss/shared/data-access';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private auth = inject(AuthService);
  private authStorage = inject(AuthStorageService);

  /**
   * Get corporate navigation structure
   * Returns the side-nav groups configuration for the corporate app
   * Matches Figma design structure exactly
   */
  getCorporateNavigation(): SideNavGroup[] {
    return [
      {
        label: '',
        items: [
          {
            label: 'Home',
            value: 'home',
            iconName: 'phosphorHouse',
            selected: false
          },
          {
            label: 'Notifications',
            value: 'notifications',
            iconName: 'phosphorBell',
            badge: 0,
            showBadge: false,
            selected: false
          },
          {
            label: 'Facilities',
            value: 'facilities',
            iconName: 'phosphorBuildings',
            selected: false
          },
          {
            label: 'Weekly Invoices',
            value: 'invoices',
            iconName: 'phosphorReceipt',
            selected: false
          },
          {
            label: 'Subscriptions',
            value: 'subscriptions',
            iconName: 'phosphorNewspaperClippingBold',
            selected: false
          },
          {
            label: 'Administrators',
            value: 'admins',
            iconName: 'phosphorUserPlus',
            badge: 0,
            showBadge: false,
            selected: false
          },
          {
            label: 'SOP / Rates',
            value: 'rates',
            iconName: 'phosphorPercent',
            selected: false
          },
          {
            label: 'Billing',
            value: 'billing',
            iconName: 'phosphorCreditCard',
            selected: false
          },
          {
            label: 'Corporate Profile',
            value: 'profile',
            iconName: 'phosphorBuildings',
            selected: false
          }
        ]
      },

      {
        label: '',
        items: [
          {
            label: 'User',
            value: 'user',
            iconName: 'phosphorQuestion',
            selected: false
          },
          {
            label: 'Sign Out',
            value: 'signOut',
            iconName: 'phosphorSignOut',
            selected: false
          }
        ]
      }
    ];
  }

  /**
   * Get current user information for side-nav display
   * Integrates with AuthService to get real user data
   * Returns Observable to support async user data loading
   */
  getCurrentUser(): SideNavUser | undefined {
    const authState = this.authStorage.state;

    if (!authState.lastSessionAt || !authState.user) {
      return undefined;
    }

    const user = authState.user;

    return {
      name: `${user.firstName} ${user.lastName}`,
      role: this.formatRole(authState.role),
      avatarSrc: undefined, // Can be extended to fetch from user profile API
      initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    };
  }

  /**
   * Get current user as Observable
   * Useful for reactive updates when auth state changes
   */
  getCurrentUser$(): Observable<SideNavUser | undefined> {
    return this.authStorage.state$.pipe(
      map((authState) => {
        if (!authState.lastSessionAt || !authState.user) {
          return undefined;
        }

        const user = authState.user;

        return {
          name: `${user.firstName} ${user.lastName}`,
          role: this.formatRole(authState.role),
          avatarSrc: undefined,
          initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
        };
      })
    );
  }

  /**
   * Get route path for a navigation item value
   * Maps nav item values to actual routes
   */
  getRouteForValue(value: string): string {
    const routeMap: Record<string, string> = {
      home: '/dashboard',
      notifications: '/notifications',
      invoices: '/invoices',
      subscriptions: '/subscriptions',
      admins: '/admins',
      rates: '/billable-rates',
      billing: '/billing',
      profile: '/profile',
      //TODO: Create a Goodbye Page
      signOut: '/auth/login',
      facilities: '/facilities'
    };

    return routeMap[value] || '/dashboard';
  }

  /**
   * Update selected nav item based on current route
   * Useful for highlighting active navigation item
   */
  updateSelectedItem(
    currentRoute: string,
    groups: SideNavGroup[]
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => ({
        ...item,
        selected: this.getRouteForValue(item.value) === currentRoute
      }))
    }));
  }

  /**
   * Update badge count for a specific nav item
   * Useful for notifications, pending items, etc.
   */
  updateBadgeCount(
    groups: SideNavGroup[],
    itemValue: string,
    count: number
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.value === itemValue) {
          return {
            ...item,
            badge: count,
            showBadge: count > 0
          };
        }
        return item;
      })
    }));
  }

  /**
   * Get navigation filtered by user role/permissions
   * Can be extended to hide nav items based on user permissions
   */
  getNavigationByRole(): SideNavGroup[] {
    const authState = this.authStorage.state;
    const allNavigation = this.getCorporateNavigation();

    // For now, return all navigation
    // Can be extended to filter based on role:
    // if (authState.role === 'FacilityAdmin') {
    //   return allNavigation; // Show all
    // } else if (authState.role === 'ShiftAdministrator') {
    //   return filterNavigation(allNavigation, ['dashboard', 'scheduling']);
    // }

    return allNavigation;
  }

  /**
   * Format user role for display
   * Converts role enum to human-readable format
   */
  private formatRole(role: string | null): string {
    if (!role) return 'User';

    // Convert camelCase/PascalCase to Title Case with spaces
    return role
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  }
}
